import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

/* ------------------------------------------------------------------ */
/*  custom scrollbar styles — injected once, not per modal             */
/* ------------------------------------------------------------------ */

let stylesInjected = false;
function ensureGlobalStyles() {
  if (stylesInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.setAttribute("data-modal-styles", "true");
  el.textContent = `
    .modal-scroll {
      scrollbar-width: thin;
      scrollbar-color: #d4d4d8 transparent;
      scrollbar-gutter: stable;
    }
    .modal-scroll::-webkit-scrollbar {
      width: 6px;
    }
    .modal-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .modal-scroll::-webkit-scrollbar-thumb {
      background-color: #d4d4d8;
      border-radius: 9999px;
    }
    .dark .modal-scroll {
      scrollbar-color: #52525b transparent;
    }
    .dark .modal-scroll::-webkit-scrollbar-thumb {
      background-color: #52525b;
    }
  `;
  document.head.appendChild(el);
  stylesInjected = true;
}

/**
 * Modal / Dialog
 * Rendered via a portal directly into document.body so the overlay is
 * always positioned against the true viewport — not a transformed /
 * filtered ancestor somewhere up the tree that would otherwise break
 * `fixed` positioning and let content (e.g. a top bar) escape the overlay.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {string} [title]
 * @param {React.ReactNode} children
 * @param {React.ReactNode} [footer]
 * @param {string} [className]
 */
export function Modal({ isOpen, onClose, title, children, footer, className }) {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Keep a live ref to the latest onClose without making the focus-trap
  // effect below depend on it. onClose is very often an inline/new function
  // identity on every parent render (e.g. wrapped in a local handler that
  // also resets form state) — if the effect depended on it directly, any
  // re-render of the modal's content (including typing in a form field)
  // would tear down and re-run the focus trap, which yanks focus off
  // whatever the user is actively typing into. Only `isOpen` should control
  // this effect's lifecycle.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    ensureGlobalStyles();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement;
    panelRef.current?.focus();

    // Lock background scroll while the modal is open.
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onCloseRef.current?.();
        return;
      }
      if (event.key === "Tab") {
        const focusable = panelRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-neutral-900/40"
        onClick={() => onCloseRef.current?.()}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        tabIndex={-1}
        className={cn(
          "relative z-10 flex w-full sm:max-w-lg flex-col overflow-hidden",
          "max-h-[100dvh] sm:max-h-[85vh]",
          "rounded-t-card sm:rounded-card bg-white dark:bg-neutral-900",
          "shadow-popover dark:shadow-black/40 focus:outline-none",
          className
        )}
      >
        {title && (
          <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 dark:border-neutral-800 px-4 py-3 sm:px-6 sm:py-4">
            <h2
              id="modal-title"
              className="text-base font-semibold text-neutral-900 dark:text-neutral-100 truncate pr-4"
            >
              {title}
            </h2>
            <button
              type="button"
              aria-label="Close dialog"
              onClick={() => onCloseRef.current?.()}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="modal-scroll flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6">
          {children}
        </div>
        {footer && (
          <div className="flex shrink-0 flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 border-t border-neutral-200 dark:border-neutral-800 px-4 py-3 sm:px-6 sm:py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}